// Angular
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Libs
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
// Models
import { ISession } from '@core/models-v2/auth/session.interface';
import { IWishlist } from '@core/services-v2/wishlist/models/wishlist-response.interface';
// Services
import { isVacio } from '../../utils/utilidades';
import { SessionService } from '@core/services-v2/session/session.service';
import { WishlistApiService } from '@core/services-v2/wishlist/wishlist-api.service';
import {
  IProductFromFile,
  IRegisteredProductFromFile,
} from '@core/services-v2/wishlist/models/product-from-file-response.interface';

@Component({
  selector: 'app-agregar-lista-productos-masiva-modal',
  templateUrl: './agregar-lista-productos-masiva-modal.component.html',
  styleUrls: ['./agregar-lista-productos-masiva-modal.component.scss'],
})
export class AgregarListaProductosMasivaModalComponent implements OnInit {
  lista!: IWishlist; // Lista pasada desde el componente padre
  productosCargados: IRegisteredProductFromFile[] = [];
  productosNoCargados: IProductFromFile[] = [];

  file: File | null = null; // Permitir valores nulos
  procesandoExcel = false;
  procesado = false;

  usuario!: ISession;
  form!: FormGroup;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private messageService: MessageService,
    // Services V2
    private readonly sessionService: SessionService,
    private readonly wishlistApiService: WishlistApiService
  ) {}

  ngOnInit() {
    this.usuario = this.sessionService.getSession();
    this.form = this.fb.group({
      file: [{ value: '', disabled: false }, Validators.required],
    });

    // Obtener la lista desde la configuración del modal
    if (this.config.data?.lista) {
      this.lista = this.config.data.lista;
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de configuración',
        detail: 'No se especificó una lista de destino.',
        life: 5000,
        icon: 'pi pi-times-circle',
      });
      this.close(false);
    }
  }

  onFileChange(event: any): void {
    if (event.target.files.length) {
      const file = event.target.files[0];
      this.file = file;
    }
  }

  /**
   * Remover archivo seleccionado
   */
  removeFile(): void {
    this.file = null;
    this.form.reset();
  }

  downloadTemplate(): void {
    const link = document.createElement('a');
    link.href =
      '../../../../assets/excel/base_carga_masiva_lista_productos.xlsx';
    link.download = 'plantilla_carga_masiva.xlsx';
    link.target = '_blank';
    link.click();
  }

  canProcess(): boolean {
    return !!(this.file && this.lista);
  }

  procesarArchivo(): void {
    if (this.procesandoExcel || !this.canProcess()) {
      return;
    }

    if (!this.file) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Archivo requerido',
        detail: 'Debe seleccionar un archivo Excel para procesar.',
        life: 4000,
        icon: 'pi pi-exclamation-triangle',
      });
      return;
    }

    this.procesandoExcel = true;
    this.form.get('file')?.disable();

    this.wishlistApiService
      .addProductsFromFileToWishlist({
        documentId: this.usuario.documentId,
        wishlistId: this.lista.id,
        file: this.file,
      })
      .subscribe({
        next: (res) => {
          this.productosCargados = res.registered;
          this.productosNoCargados = res.notFound;

          // Mostrar mensaje de resultado
          if (
            this.productosCargados.length &&
            !this.productosNoCargados.length
          ) {
            this.messageService.add({
              severity: 'success',
              summary: 'Procesamiento exitoso',
              detail: `Se agregaron ${this.productosCargados.length} productos a "${this.lista.name}".`,
              life: 5000,
              icon: 'pi pi-check-circle',
            });
          } else if (
            this.productosCargados.length &&
            this.productosNoCargados.length
          ) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Procesamiento parcial',
              detail: `Se agregaron ${this.productosCargados.length} de ${
                this.productosCargados.length + this.productosNoCargados.length
              } productos a "${this.lista.name}".`,
              life: 5000,
              icon: 'pi pi-exclamation-triangle',
            });
          } else if (
            !this.productosCargados.length &&
            this.productosNoCargados.length
          ) {
            this.messageService.add({
              severity: 'error',
              summary: 'Procesamiento fallido',
              detail:
                'No se pudo agregar ningún producto. Verifique el archivo.',
              life: 5000,
              icon: 'pi pi-times-circle',
            });
          }

          this.procesandoExcel = false;
          this.form.get('file')?.enable();
          this.procesado = true;
          this.form.reset();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error de procesamiento',
            detail: `Ha ocurrido un error al agregar productos a "${this.lista.name}".`,
            life: 5000,
            icon: 'pi pi-times-circle',
          });
          this.procesandoExcel = false;
          this.form.get('file')?.enable();
          this.form.reset();
          console.error('Error processing file:', error);
        },
      });
  }

  close(hasChanges: boolean): void {
    this.ref.close(hasChanges);
  }
}
